import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type CSSProperties,
} from "react";
import { getAdventure, resolveAdventureImages } from "../content";
import type { AdventureOption } from "../types/adventure";
import type { ChallengeMode } from "./ChallengePanel";
import {
  createSummary,
  gameReducer,
  INITIAL_STATE,
  OUTCOME_MS,
  REVEAL_MS,
  REVEAL_PAUSE_MS,
} from "../lib/game-state";
import { AdventureSelect } from "./AdventureSelect";
import { CrashScreen } from "./CrashScreen";
import { DebugPanel } from "./DebugPanel";
import { GameStage } from "./GameStage";
import { LessonScreen } from "./LessonScreen";
import { SummaryScreen } from "./SummaryScreen";

interface GameProps {
  adventures: ReturnType<typeof import("../content").getAdventures>;
  isPaid: boolean;
  portalUrl?: string;
  onPaywall: (feature: string) => void;
}

export function Game({ adventures, isPaid, portalUrl, onPaywall }: GameProps) {
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showPositionDebug, setShowPositionDebug] = useState(false);
  const [showLessonPreview, setShowLessonPreview] = useState(false);

  const [state, dispatch] = useReducer(
    (s: typeof INITIAL_STATE, action: Parameters<typeof gameReducer>[1]) => {
      const adventure = s.adventureId ? getAdventure(s.adventureId) ?? null : null;
      return gameReducer(s, action, adventure);
    },
    INITIAL_STATE
  );

  const adventure = state.adventureId ? getAdventure(state.adventureId) : null;
  const previewAdventure =
    adventure ?? adventures.find((a) => a.id === "ruimtereis") ?? adventures[0] ?? null;

  const lessonPreviewOverlay =
    showLessonPreview && previewAdventure && state.phase !== "lesson" ? (
      <LessonScreen
        explanation={previewAdventure.actExplanation}
        onContinue={() => setShowLessonPreview(false)}
      />
    ) : null;
  const images = useMemo(
    () => (adventure ? resolveAdventureImages(adventure) : {}),
    [adventure]
  );

  const currentRound = adventure?.rounds[state.roundIndex];
  const roundNumber = currentRound?.n ?? 5;

  const handleChoose = useCallback(
    (option: AdventureOption) => {
      if (state.phase === "choose") {
        dispatch({
          type: "FINISH_CHOICE",
          optionId: option.id,
          best: option.best,
          outcome: option.outcome,
        });
      } else {
        dispatch({
          type: "CHOOSE_OPTION",
          optionId: option.id,
          best: option.best,
          outcome: option.outcome,
        });
      }
    },
    [state.phase]
  );

  const inActiveGame =
    state.phase === "playing" ||
    state.phase === "crash" ||
    state.phase === "lesson" ||
    state.phase === "defuse" ||
    state.phase === "choose";

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setShowDebugPanel((on) => !on);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Global game timer — ticks continuously during playing
  useEffect(() => {
    if (state.phase !== "playing") return;
    const id = window.setInterval(() => dispatch({ type: "TICK" }), 1000);
    return () => window.clearInterval(id);
  }, [state.phase]);

  // Round sequencing: reveal -> challenge -> outcome -> advance
  useEffect(() => {
    if (state.phase !== "playing") return;

    if (state.stage === "reveal") {
      if (state.revealedCount < state.thoughts.length) {
        const id = window.setTimeout(
          () => dispatch({ type: "REVEAL_NEXT" }),
          REVEAL_MS
        );
        return () => window.clearTimeout(id);
      }
      const id = window.setTimeout(
        () => dispatch({ type: "BEGIN_CHALLENGE" }),
        REVEAL_PAUSE_MS
      );
      return () => window.clearTimeout(id);
    }

    if (state.stage === "outcome") {
      const id = window.setTimeout(
        () => dispatch({ type: "ADVANCE_ROUND" }),
        OUTCOME_MS
      );
      return () => window.clearTimeout(id);
    }
  }, [
    state.phase,
    state.stage,
    state.revealedCount,
    state.thoughts.length,
  ]);

  if (state.phase === "select") {
    return (
      <>
        <AdventureSelect
          adventures={adventures}
          isPaid={isPaid}
          onSelect={(id) => dispatch({ type: "SELECT_ADVENTURE", adventureId: id })}
          onLocked={(label) => onPaywall(`het avontuur "${label}"`)}
        />
        {lessonPreviewOverlay}
      </>
    );
  }

  if (!adventure) return null;

  if (state.phase === "intro") {
    return (
      <>
        <section className="act-intro jari-card" style={adventure.theme as CSSProperties}>
          <span className="act-intro__icon" aria-hidden>
            {adventure.goal.icon}
          </span>
          <h2>{adventure.label}</h2>
          <p>{adventure.tagline}</p>
          <p className="act-intro__goal">
            Jouw doel: <strong>{adventure.goal.name}</strong>
          </p>
          <p className="act-intro__hint">
            Onderweg krijg je signalen van je systemen — dat zijn je gedachten. Sommige
            helpen, andere niet. Luister vooral naar de helpende!
          </p>
          <button
            type="button"
            className="jari-btn jari-btn--primary"
            onClick={() => dispatch({ type: "START_GAME" })}
          >
            Start avontuur
          </button>
          <button
            type="button"
            className="jari-btn"
            onClick={() => dispatch({ type: "BACK_TO_SELECT" })}
          >
            Terug
          </button>
        </section>
        {lessonPreviewOverlay}
      </>
    );
  }

  if (state.phase === "summary") {
    return (
      <>
        <SummaryScreen
          adventure={adventure}
          summary={createSummary(adventure, state)}
          isPaid={isPaid}
          portalUrl={portalUrl}
          onRestart={() => dispatch({ type: "BACK_TO_SELECT" })}
        />
        {lessonPreviewOverlay}
      </>
    );
  }

  const selectMode = state.phase === "defuse";
  const focusMode = state.phase === "choose";

  let challengeMode: ChallengeMode | null = null;
  if (state.phase === "playing") {
    if (state.stage === "challenge") challengeMode = "challenge";
    else if (state.stage === "outcome") challengeMode = "outcome";
  } else if (focusMode) {
    challengeMode = "choose";
  }

  const challengeRound =
    focusMode || selectMode ? adventure.rounds[4] : currentRound;

  const showTimer = state.phase === "playing";

  return (
    <div className="act-game" style={adventure.theme as CSSProperties}>
      <GameStage
        adventure={adventure}
        images={images}
        thoughts={state.thoughts}
        revealedCount={state.revealedCount}
        roundNumber={roundNumber}
        phase={state.phase}
        timeLeft={state.timeLeft}
        showTimer={showTimer}
        selectMode={selectMode}
        focusMode={focusMode}
        selectedHelpfulIds={state.selectedHelpfulIds}
        selectPrompt={selectMode ? adventure.defusePrompt : undefined}
        onToggleHelpful={(thoughtId) =>
          dispatch({ type: "TOGGLE_HELPFUL_SELECTION", thoughtId })
        }
        challenge={
          challengeMode && challengeRound
            ? {
                mode: challengeMode,
                text: challengeRound.challenge,
                options: challengeRound.options,
                blocked: state.buttonsBlocked,
                lastOutcome: state.lastOutcome,
                lastBest: state.lastBest,
                onChoose: handleChoose,
              }
            : undefined
        }
        showPositionDebug={showPositionDebug}
      />

      {state.phase === "crash" && (
        <CrashScreen
          text={adventure.crashText}
          onContinue={() => dispatch({ type: "CRASH_CONTINUE" })}
        />
      )}

      {state.phase === "lesson" && (
        <LessonScreen
          explanation={adventure.actExplanation}
          onContinue={() => dispatch({ type: "LESSON_CONTINUE" })}
        />
      )}

      {lessonPreviewOverlay}

      {showDebugPanel && inActiveGame && adventure && (
        <DebugPanel
          showLessonPreview={showLessonPreview}
          showPositionDebug={showPositionDebug}
          activeRoundIndex={state.phase === "playing" ? state.roundIndex : null}
          activePhase={
            state.phase === "crash" ||
            state.phase === "lesson" ||
            state.phase === "defuse" ||
            state.phase === "choose"
              ? state.phase
              : null
          }
          roundCount={adventure.rounds.length}
          onToggleLesson={() => setShowLessonPreview((on) => !on)}
          onTogglePosition={() => setShowPositionDebug((on) => !on)}
          onJumpRound={(roundIndex) =>
            dispatch({ type: "DEBUG_JUMP_ROUND", roundIndex })
          }
          onJumpPhase={(phase) => dispatch({ type: "DEBUG_JUMP_PHASE", phase })}
        />
      )}

      <button
        type="button"
        className="act-game__quit jari-btn"
        onClick={() => dispatch({ type: "BACK_TO_SELECT" })}
      >
        Stoppen
      </button>
    </div>
  );
}
